import { useCallback } from 'react';
import { addDays, isAfter, isBefore, isEqual, subDays } from 'date-fns';

import { clampDateToBounds } from '../utils';

/**
 * 날짜 범위 변경 핸들러를 제공하는 커스텀 훅
 *
 * @description
 * - startDate 변경 시: endDate보다 늦거나 같으면 endDate를 startDate + 1일로 자동 조정 (maxDate 초과 시 maxDate로 clamp하고 startDate를 한 단계 앞으로 밀어 start < end 유지)
 * - endDate 변경 시: startDate보다 이르거나 같으면 startDate를 endDate - 1일로 자동 조정 (minDate 미만 시 minDate로 clamp하고 endDate를 한 단계 뒤로 밀어 start < end 유지)
 * - null 값 처리: startDate null → 오늘, endDate null → startDate + 1일
 *
 * @param validatedStartDate - 검증된 시작 날짜
 * @param validatedEndDate - 검증된 종료 날짜
 * @param onChange - 날짜 범위 변경 콜백
 * @param minDate - 선택 가능한 최소 날짜 (자동 보정 결과의 하한)
 * @param maxDate - 선택 가능한 최대 날짜 (자동 보정 결과의 상한)
 * @param allowEqualDates - true이면 start === end를 허용해 자동 +1일 보정을 건너뜁니다. (역전 보정은 유지)
 * @returns 시작/종료 날짜 변경 핸들러
 */
export const useDateRangeHandlers = (
  validatedStartDate: Date,
  validatedEndDate: Date,
  onChange?: (dates: [Date | null, Date | null]) => void,
  minDate?: Date | null,
  maxDate?: Date | null,
  allowEqualDates = false,
) => {
  const handleStartChange = useCallback(
    (date: Date | null) => {
      // date가 null이면 오늘 날짜로 설정
      const newStartDate = date || new Date();

      // newStartDate가 endDate보다 늦거나 같으면 endDate를 newStartDate + 1일로 설정
      if (
        isAfter(newStartDate, validatedEndDate) ||
        (!allowEqualDates && isEqual(newStartDate, validatedEndDate))
      ) {
        const newEndDate = clampDateToBounds(addDays(newStartDate, 1), minDate, maxDate);
        // newEndDate가 경계에 막혀 newStartDate 이하가 된 경우, newStartDate를 한 단계 앞으로 밀어 start < end 유지
        if (isBefore(newEndDate, newStartDate) || isEqual(newEndDate, newStartDate)) {
          onChange?.([subDays(newEndDate, 1), newEndDate]);
        } else {
          onChange?.([newStartDate, newEndDate]);
        }
      } else {
        onChange?.([newStartDate, validatedEndDate]);
      }
    },
    [validatedEndDate, onChange, minDate, maxDate, allowEqualDates],
  );

  const handleEndChange = useCallback(
    (date: Date | null) => {
      // date가 null이면 startDate + 1일로 설정
      let newEndDate = date;
      if (!newEndDate) {
        newEndDate = addDays(validatedStartDate, 1);
      }

      // newEndDate가 startDate보다 이르거나 같으면 startDate를 newEndDate - 1일로 설정
      if (
        isBefore(newEndDate, validatedStartDate) ||
        (!allowEqualDates && isEqual(newEndDate, validatedStartDate))
      ) {
        const newStartDate = clampDateToBounds(subDays(newEndDate, 1), minDate, maxDate);
        // newStartDate가 경계에 막혀 newEndDate 이상이 된 경우, newEndDate를 한 단계 뒤로 밀어 start < end 유지
        if (isAfter(newStartDate, newEndDate) || isEqual(newStartDate, newEndDate)) {
          onChange?.([newStartDate, addDays(newStartDate, 1)]);
        } else {
          onChange?.([newStartDate, newEndDate]);
        }
      } else {
        onChange?.([validatedStartDate, newEndDate]);
      }
    },
    [validatedStartDate, onChange, minDate, maxDate, allowEqualDates],
  );

  return { handleStartChange, handleEndChange };
};
